import React, {useState, useEffect, useRef} from 'react'
import {CampaignChannel, ChannelVariant, ChannelVariantInput} from '../../../types/campaigns'
import {HTMLEditor} from './HTMLEditor'
import {WhatsAppEditor} from './tiptap/WhatsAppEditor'
import {WhatsAppPreview} from './tiptap/WhatsAppPreview'

interface VariantEditorProps {
  channel: CampaignChannel
  variant: ChannelVariant | null
  onSave: (variantData: ChannelVariantInput) => void
  onDelete?: () => void
  templateVariables: string[]
  onBodyTextChange?: (bodyText: string) => void
}

export const VariantEditor: React.FC<VariantEditorProps> = ({
  channel,
  variant,
  onSave,
  onDelete,
  templateVariables,
  onBodyTextChange,
}) => {
  const [subject, setSubject] = useState(variant?.subject || '')
  const [bodyText, setBodyText] = useState(variant?.bodyText || '')

  // Notify parent of bodyText changes for real-time preview
  useEffect(() => {
    if (onBodyTextChange) {
      onBodyTextChange(bodyText)
    }
  }, [bodyText, onBodyTextChange])
  const [bodyHtml, setBodyHtml] = useState(variant?.bodyHtml || '')
  const [whatsappTemplateId, setWhatsappTemplateId] = useState(variant?.whatsappTemplateId || '')
  const [whatsappLanguage, setWhatsappLanguage] = useState(variant?.whatsappLanguage || 'es')
  const [smsMaxLength, setSmsMaxLength] = useState(variant?.smsMaxLength || 160)
  const [isDefault, setIsDefault] = useState(variant?.isDefault || false)
  const [useHtml, setUseHtml] = useState(false)

  const bodyTextRef = useRef<HTMLTextAreaElement>(null)
  const subjectRef = useRef<HTMLInputElement>(null)

  // Reset form when channel or variant changes
  useEffect(() => {
    setSubject(variant?.subject || '')
    setBodyText(variant?.bodyText || '')
    setBodyHtml(variant?.bodyHtml || '')
    setWhatsappTemplateId(variant?.whatsappTemplateId || '')
    setWhatsappLanguage(variant?.whatsappLanguage || 'es')
    setSmsMaxLength(variant?.smsMaxLength || 160)
    setIsDefault(variant?.isDefault || false)
    setUseHtml(!!variant?.bodyHtml)
  }, [variant, channel])

  const handleSave = () => {
    const variantData: ChannelVariantInput = {
      channel,
      subject,
      bodyText,
      bodyHtml: channel === 'EMAIL' && useHtml ? bodyHtml : undefined,
      whatsappTemplateId: channel === 'WHATSAPP' ? whatsappTemplateId : '', // Default empty string for non-WhatsApp
      whatsappLanguage: channel === 'WHATSAPP' ? whatsappLanguage : 'es', // Default 'es' for non-WhatsApp
      smsMaxLength: channel === 'SMS' ? smsMaxLength : 160, // Default 160 for non-SMS channels
      isDefault,
    }
    onSave(variantData)
  }

  const renderChannelSpecificFields = () => {
    switch (channel) {
      case 'SMS':
        return (
          <div className='mb-5'>
            <label className='form-label'>Longitud Máxima</label>
            <input
              type='number'
              className='form-control'
              value={smsMaxLength}
              onChange={(e) => setSmsMaxLength(parseInt(e.target.value))}
            />
            <div className='form-text'>
              Caracteres actuales: {bodyText.length} / {smsMaxLength}
              {bodyText.length > smsMaxLength && (
                <span className='text-danger ms-2'>⚠️ Excede el límite</span>
              )}
            </div>
          </div>
        )

      case 'EMAIL':
        return (
          <>
            <div className='mb-5'>
              <label className='form-label required'>Asunto</label>
              <input
                ref={subjectRef}
                type='text'
                className='form-control'
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder='Ej: Recordatorio de pago'
              />
            </div>

            <div className='mb-5'>
              <div className='form-check form-switch mb-3'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='useHtmlSwitch'
                  checked={useHtml}
                  onChange={(e) => setUseHtml(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='useHtmlSwitch'>
                  Usar HTML para el cuerpo del email
                </label>
              </div>

              {useHtml && (
                <>
                  <div className='alert alert-info small'>
                    <strong>💡 Tip:</strong> When using HTML mode, use{' '}
                    <code>{'{{variable_name}}'}</code> directly in your HTML code.
                  </div>
                  <HTMLEditor value={bodyHtml} onChange={setBodyHtml} height={350} variables={templateVariables} />
                </>
              )}
            </div>
          </>
        )

      case 'WHATSAPP':
        return (
          <>
            <div className='alert alert-info mb-5'>
              <strong>💡 Templates de Meta WhatsApp</strong>
              <p className='mb-2'>
                Para campañas de marketing, debes usar templates aprobados por Meta.
              </p>
              <ul className='mb-0'>
                <li>Crea templates en Meta Business Manager</li>
                <li>Espera aprobación (24-48h)</li>
                <li>Ingresa el ID aquí</li>
              </ul>
            </div>

            <div className='mb-5'>
              <label className='form-label'>Template ID de Meta (opcional)</label>
              <input
                type='text'
                className='form-control'
                value={whatsappTemplateId}
                onChange={(e) => setWhatsappTemplateId(e.target.value)}
                placeholder='Ej: payment_reminder'
              />
              <div className='form-text'>
                Si no usas template, solo funcionará en sesiones activas (24h)
              </div>
            </div>

            <div className='mb-5'>
              <label className='form-label'>Idioma</label>
              <select
                className='form-select'
                value={whatsappLanguage}
                onChange={(e) => setWhatsappLanguage(e.target.value)}
              >
                <option value='es'>Español</option>
                <option value='en'>English</option>
                <option value='pt'>Português</option>
              </select>
            </div>
          </>
        )

      default:
        return null
    }
  }

  return (
    <div className='card'>
      <div className='card-header'>
        <h3 className='card-title'>
          {variant ? 'Editar Variante' : 'Nueva Variante'} - {channel}
        </h3>
      </div>
      <div className='card-body'>
        {/* Channel-specific fields */}
        {renderChannelSpecificFields()}

        {/* Common field: Body Text */}
        {!(channel === 'EMAIL' && useHtml) && (
          <div className='mb-5'>
            <label className='form-label required'>
              Cuerpo del Mensaje
              {channel === 'EMAIL' && ' (Texto Plano)'}
            </label>
            
            {/* Use WhatsApp Editor for WHATSAPP channel */}
            {channel === 'WHATSAPP' ? (
              <>
                <WhatsAppEditor
                  value={bodyText}
                  onChange={setBodyText}
                  height={300}
                  placeholder='Ej: Hola {{client_first_name}}, tu pago vence el {{policy_effective_date}}'
                  variables={templateVariables}
                />
                <div className='form-text'>
                  <strong>💡 Tip:</strong> Usa el botón <strong>Variables</strong> en la barra de herramientas para insertar variables fácilmente.
                  También puedes escribir manualmente usando <code>{'{{variable_name}}'}</code>
                </div>
              </>
            ) : (
              <>
                <textarea
                  ref={bodyTextRef}
                  className='form-control'
                  rows={channel === 'SMS' ? 4 : 8}
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  placeholder={`Ej: Hola {{client_first_name}}, tu pago vence el {{policy_effective_date}}`}
                />
                <div className='form-text'>
                  <strong>Tip:</strong> Click on variables above to insert them, or type manually using{' '}
                  <code>{'{{variable_name}}'}</code> format
                </div>
              </>
            )}
          </div>
        )}

        {/* Default variant checkbox */}
        <div className='mb-5'>
          <div className='form-check'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              id='isDefaultCheck'
            />
            <label className='form-check-label' htmlFor='isDefaultCheck'>
              Marcar como variante predeterminada
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className='d-flex justify-content-between'>
          <button onClick={handleSave} className='btn btn-primary'>
            {variant ? '💾 Actualizar' : '➕ Crear'} Variante
          </button>

          {variant && onDelete && (
            <button onClick={onDelete} className='btn btn-danger'>
              🗑️ Eliminar Variante
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
